import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as FiIcons from "react-icons/fi";
const { FiMail, FiLock, FiShield, FiArrowRight, FiCheck, FiAlertCircle } =
  FiIcons;
import SafeIcon from "../common/SafeIcon";
import api from "../lib/api";
import toast from "react-hot-toast";

export default function AdminInviteEnrollment() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [inviteToken, setInviteToken] = useState("");

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
  });

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      const token = searchParams.get("token");
      if (!token) {
        toast.error("No invite token provided");
        setValidating(false);
        return;
      }

      setInviteToken(token);

      try {
        const response = await api.post("/admin/invite/verify-token", {
          token,
        });
        if (response.data.valid) {
          setTokenValid(true);
        } else {
          toast.error("Invalid or expired invite token");
        }
      } catch (err) {
        toast.error(
          err.response?.data?.error ||
            "Failed to validate invite token. Please try again.",
        );
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post("/admin/invite/register", {
        token: inviteToken,
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
      });

      if (response.data.success) {
        // Store token and redirect to admin dashboard
        localStorage.setItem("token", response.data.token);
        toast.success(
          `Welcome, ${response.data.user.first_name}! Admin account created.`,
        );
        navigate("/admin");
      }
    } catch (err) {
      console.error("Enrollment failed:", err);
      toast.error(
        err.response?.data?.error || "Admin registration failed. Try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-b-2 border-primary-500 rounded-full mx-auto mb-4" />
          <p className="text-slate-400">Validating invite token...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-slate-900/20 border border-red-500/30 backdrop-blur-2xl rounded-3xl p-8 shadow-2xl text-center"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center">
              <SafeIcon
                icon={FiAlertCircle}
                className="text-red-500 text-3xl"
              />
            </div>
          </div>
          <h2 className="text-2xl font-black text-white mb-3">
            Invalid Invite
          </h2>
          <p className="text-slate-400 text-sm mb-6">
            This invite token is invalid, expired, or has already been used.
            Contact your system administrator to request a new one.
          </p>
          <Link
            to="/signin"
            className="inline-block bg-primary-600 hover:bg-primary-500 text-white font-black py-3 px-6 rounded-2xl transition-all"
          >
            Return to Sign In
          </Link>
        </motion.div>
      </div>
    );
  }

  // Valid token - show enrollment form
  return (
    <div className="min-h-screen bg-[#020617] flex overflow-hidden relative">
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary-500/10 blur-[120px] rounded-full" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-500/10 blur-[120px] rounded-full" />

      <div className="w-full flex items-center justify-center p-8 md:p-16 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-[440px] w-full"
        >
          <div className="mb-10 text-center">
            <Link
              to="/"
              className="flex items-center gap-3 justify-center mb-6"
            >
              <div className="w-10 h-10 bg-primary-500 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiShield} className="text-white text-xl" />
              </div>
              <span className="text-2xl font-black tracking-tighter text-white">
                iVivaCore
              </span>
            </Link>
            <h1 className="text-3xl font-black text-white mb-3">
              Admin Enrollment
            </h1>
            <p className="text-slate-400 font-medium">
              Complete your admin account setup.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-[0.2em]">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) =>
                    setFormData({ ...formData, first_name: e.target.value })
                  }
                  required
                  placeholder="First"
                  className="w-full bg-slate-900/50 border border-slate-800 rounded-2xl px-4 py-3 text-white focus:border-primary-500/50 outline-none transition-all font-medium text-sm"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-[0.2em]">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) =>
                    setFormData({ ...formData, last_name: e.target.value })
                  }
                  required
                  placeholder="Last"
                  className="w-full bg-slate-900/50 border border-slate-800 rounded-2xl px-4 py-3 text-white focus:border-primary-500/50 outline-none transition-all font-medium text-sm"
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="text-[11px] font-black text-slate-500 uppercase tracking-[0.2em]">
                Email Address
              </label>
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-primary-500 transition-colors">
                  <SafeIcon icon={FiMail} />
                </div>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                  placeholder="admin@institution.com"
                  className="w-full bg-slate-900/50 border border-slate-800 rounded-2xl pl-12 pr-4 py-3 text-white focus:border-primary-500/50 focus:ring-4 focus:ring-primary-500/5 transition-all outline-none placeholder:text-slate-600 font-medium"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label className="text-[11px] font-black text-slate-500 uppercase tracking-[0.2em]">
                Secure Password
              </label>
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-primary-500 transition-colors">
                  <SafeIcon icon={FiLock} />
                </div>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required
                  placeholder="Min. 8 characters"
                  className="w-full bg-slate-900/50 border border-slate-800 rounded-2xl pl-12 pr-4 py-3 text-white focus:border-primary-500/50 focus:ring-4 focus:ring-primary-500/5 transition-all outline-none placeholder:text-slate-600 font-medium font-mono"
                />
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-600 hover:bg-primary-500 text-white font-black py-3 rounded-2xl flex items-center justify-center gap-3 transition-all shadow-xl shadow-primary-900/20 active:scale-[0.98] disabled:opacity-50 group mt-8"
            >
              {loading ? (
                "Completing Setup..."
              ) : (
                <>
                  <SafeIcon
                    icon={FiArrowRight}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                  Complete Admin Setup
                </>
              )}
            </button>
          </form>

          <div className="mt-8 p-4 bg-slate-900/50 border border-slate-800 rounded-2xl">
            <p className="text-[11px] text-slate-400 font-medium leading-relaxed">
              <SafeIcon
                icon={FiCheck}
                className="text-primary-500 inline mr-2"
              />
              This account will have full system administration access.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
